import { useEffect, useMemo, useRef, useState } from 'react'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Badge from 'react-bootstrap/Badge'
import Spinner from 'react-bootstrap/Spinner'
import Alert from 'react-bootstrap/Alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes, faPen, faSave, faCheckDouble, faChevronUp, faChevronDown, faUndo, faPlus, faLink, faTrash, faRedo, faMagic } from '@fortawesome/free-solid-svg-icons'
import renderDescription from 'shared/render-description'
import { confidenceBg } from './confidenceUtils'
import AnalyticSearch from './AnalyticSearch'
import ComposeAnalyticModal from './ComposeAnalyticModal'

// FontAwesome link glyph as a data-URI, tinted to the theme primary, used to flag
// document highlights that are linked to an analytic.
const FA_LINK_DATA_URI = (() => {
  const [w, h, , , path] = faLink.icon
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}"><path fill="#1E2E48" d="${path}"/></svg>`
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
})()

// Marks are emitted by PolicyHtmlRenderer as <mark data-rule-id="N"> and styled
// here by the rule's review status. The "current" rule under review gets an
// extra ring so the auto-scroll target is obvious (DocuSign-style).
const MARK_STYLES = `
/* Floating action/review panel anchored just beneath the selected highlight. */
.policy-floating-bar {
  position: absolute;
  /* Align with the document text column (Card.Body has ~1rem horizontal padding). */
  left: 1rem;
  right: 1rem;
  z-index: 6;
  padding: 0.85rem 1rem;
  border: 1px solid #1E2E48;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
}
.policy-doc-content mark.policy-mark {
  padding: 0.05em 0.15em;
  border-radius: 3px;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}
.policy-doc-content mark.policy-mark-pending { background: rgba(255, 224, 102, 0.5); color: inherit; }
/* Accepted = on-theme blue (a light tint of the #1E2E48 primary). */
.policy-doc-content mark.policy-mark-accepted { background: rgba(30, 46, 72, 0.16); color: inherit; }
/* Rejected = no highlight at all (plain document text). */
.policy-doc-content mark.policy-mark-rejected {
  background: transparent;
  color: inherit;
  text-decoration: none;
}
/* The selected rule: a bright highlight lifted above the blur backdrop so it pops. */
.policy-doc-content mark.policy-mark-current {
  position: relative;
  z-index: 5;
  background: #ffe14d !important;
  color: #1E2E48 !important;
  box-shadow: 0 0 0 2px #1E2E48;
  border-radius: 3px;
  scroll-margin-top: 170px;
}
/* Dim + blur the rest of the document while a panel is open. */
.policy-doc-backdrop {
  position: absolute;
  inset: 0;
  z-index: 4;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
  border-radius: inherit;
}
/* A rule whose analytics are actively being linked pulses so reviewers can see
   matching is still running — especially after "Accept All", when the review
   panel is closed and the per-rule spinner isn't visible. */
@keyframes policy-mark-linking-pulse {
  0%   { background: rgba(255, 224, 102, 0.5); }
  50%  { background: rgba(255, 224, 102, 0.9); }
  100% { background: rgba(255, 224, 102, 0.5); }
}
.policy-doc-content mark.policy-mark-linking {
  animation: policy-mark-linking-pulse 1.1s ease-in-out infinite;
}
/* A rule whose text has been linked to an analytic gets a FontAwesome link icon
   (theme-primary blue), sized to match the surrounding document text. */
.policy-doc-content mark.policy-mark-linked::after {
  content: '';
  display: inline-block;
  width: 1em;
  height: 1em;
  margin-left: 3px;
  vertical-align: -0.125em;
  background-image: url("${FA_LINK_DATA_URI}");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

/* Typography: smaller headings than Bootstrap defaults, with extra space above
   each heading so sections read as distinct blocks. */
.policy-doc-content h1,
.policy-doc-content h2,
.policy-doc-content h3,
.policy-doc-content h4,
.policy-doc-content h5,
.policy-doc-content h6 {
  font-weight: 600;
  line-height: 1.3;
  margin-top: 1.85rem;
  margin-bottom: 0.6rem;
}
.policy-doc-content h1 { font-size: 1.35rem; }
.policy-doc-content h2 { font-size: 1.18rem; }
.policy-doc-content h3 { font-size: 1.05rem; }
.policy-doc-content h4 { font-size: 0.97rem; }
.policy-doc-content h5,
.policy-doc-content h6 { font-size: 0.9rem; }
.policy-doc-content > :first-child { margin-top: 0; }
.policy-doc-content p { margin-bottom: 0.9rem; }
.policy-doc-content ul,
.policy-doc-content ol { margin-bottom: 0.9rem; }
.policy-doc-content li { margin-bottom: 0.3rem; }
.policy-doc-content hr { margin: 1.85rem 0; }
.policy-doc-content blockquote {
  margin: 1rem 0;
  padding-left: 1rem;
  border-left: 3px solid #dee2e6;
  color: #555;
}
`

// Height of the app's fixed-top navbar; the review toolbar sticks just below it.
const NAVBAR_HEIGHT = 54

// White badge with a green check and dark label, used to mark an accepted rule.
const AcceptedBadge = ({ label = 'Accepted' }) => (
  <Badge bg="" className="bg-white border" style={{ fontSize: '0.65rem', fontWeight: 'normal', color: '#1E2E48' }}>
    <FontAwesomeIcon icon={faCheck} className="text-success me-1" />{label}
  </Badge>
)

// Tags the rendered document is allowed to contain — mirrors the server-side
// PolicyHtmlRenderer::ALLOWED_TAGS allowlist.
const ALLOWED_DOC_TAGS = new Set([
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P', 'UL', 'OL', 'LI',
  'STRONG', 'EM', 'S', 'CODE', 'PRE', 'BLOCKQUOTE', 'BR', 'HR', 'MARK'
])
// Tags whose contents must be dropped entirely if they ever appear.
const DANGEROUS_DOC_TAGS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META',
  'SVG', 'MATH', 'FORM', 'INPUT', 'BUTTON', 'TEMPLATE'
])

// Defense-in-depth: the HTML is already sanitized server-side before storage, but
// it originates from AI processing of arbitrary uploaded PDF text, so we scrub it
// again on the client before injecting via dangerouslySetInnerHTML. Every attribute
// except data-rule-id is removed (killing on*-handlers, style, href/src, etc.),
// dangerous elements are deleted outright, and unknown tags are unwrapped.
const sanitizePolicyHtml = (dirty) => {
  if (!dirty) return ''
  // DOMParser does not execute scripts or load resources for the parsed document.
  const doc = new DOMParser().parseFromString(dirty, 'text/html')
  doc.body.querySelectorAll('*').forEach((el) => {
    const tag = el.tagName
    if (DANGEROUS_DOC_TAGS.has(tag)) {
      el.remove()
      return
    }
    Array.from(el.attributes).forEach((attr) => {
      if (attr.name !== 'data-rule-id') el.removeAttribute(attr.name)
    })
    if (!ALLOWED_DOC_TAGS.has(tag)) {
      el.replaceWith(...el.childNodes)
    }
  })
  return doc.body.innerHTML
}

const PolicyDocumentContent = ({
  html,
  rules = [],
  reviewTrigger = 0,
  onUpdateRule,
  onBulkAccept,
  onCreateRule,
  onPersistHtml,
  onLinkAnalytic,
  onUnlinkAnalytic,
  onRetryMatch,
  onDeleteRule,
  allAnalytics = [],
  policySectionIds = [],
  municipalityId,
  composableSections = [],
  defaultSectionId,
  linking = false
}) => {
  const containerRef = useRef(null)
  const cardRef = useRef(null)
  const floatingBarRef = useRef(null)
  const savedRangeRef = useRef(null)
  // The specific <mark> the user clicked, so a rule with several marks anchors
  // its scroll + panel to that one rather than to the first/bottom-most.
  const anchorMarkRef = useRef(null)
  // Document-space top offset for the floating panel (just below the highlight).
  const [barTop, setBarTop] = useState(0)
  const [currentRuleId, setCurrentRuleId] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editSummary, setEditSummary] = useState('')
  const [editCategory, setEditCategory] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [selection, setSelection] = useState(null)
  // Rule ids the user just accepted/created — show the linking loader only on these.
  const [linkingRuleIds, setLinkingRuleIds] = useState(() => new Set())
  // Whether the manual "link analytics" search is open for the selected rule.
  const [showLinkSearch, setShowLinkSearch] = useState(false)
  // Whether the "Compose Analytic" modal is open, and the rule it was opened
  // for. We snapshot the rule (rather than read currentRule live) so the modal
  // survives the panel collapsing — e.g. when a portaled <Select> menu click
  // lands outside the floating bar and clears currentRuleId.
  const [showCompose, setShowCompose] = useState(false)
  const [composeRule, setComposeRule] = useState(null)
  // Analytic being edited (recipe) from the linked-analytics list, if any.
  const [editAnalyticId, setEditAnalyticId] = useState(null)
  const canCompose = !!municipalityId && composableSections.length > 0

  const safeHtml = useMemo(() => sanitizePolicyHtml(html), [html])

  // Rule ids in the order their <mark>s appear in the document, so the review
  // steps through the page top-to-bottom rather than by extraction order.
  const [docOrder, setDocOrder] = useState([])

  const rulesById = useMemo(() => {
    const map = new Map()
    rules.forEach((r) => map.set(String(r.id), r))
    return map
  }, [rules])

  const orderedRules = useMemo(() => {
    const byPosition = [...rules].sort((a, b) => a.position - b.position)
    if (docOrder.length === 0) return byPosition
    const docIndex = new Map(docOrder.map((id, i) => [id, i]))
    // Stable sort: rules located in the document come first in their on-page
    // order; rules without a mark keep their relative position order at the end.
    return byPosition.sort((a, b) => {
      const ai = docIndex.has(String(a.id)) ? docIndex.get(String(a.id)) : Infinity
      const bi = docIndex.has(String(b.id)) ? docIndex.get(String(b.id)) : Infinity
      return ai - bi
    })
  }, [rules, docOrder])

  const pendingRules = orderedRules.filter((r) => r.status === 'pending')
  const acceptedCount = orderedRules.filter((r) => r.status === 'accepted').length
  const rejectedCount = orderedRules.filter((r) => r.status === 'rejected').length
  const total = orderedRules.length

  const currentRule = currentRuleId != null ? rulesById.get(String(currentRuleId)) : null
  const currentIndex = orderedRules.findIndex((r) => String(r.id) === String(currentRuleId))

  // Next pending rule in document order, wrapping around. Computed locally so it
  // works before the query refetch lands.
  const pickNext = (actedId) => {
    const actedIdx = orderedRules.findIndex((r) => String(r.id) === String(actedId))
    for (let i = actedIdx + 1; i < orderedRules.length; i += 1) {
      if (orderedRules[i].status === 'pending') return String(orderedRules[i].id)
    }
    for (let i = 0; i < orderedRules.length; i += 1) {
      if (i !== actedIdx && orderedRules[i].status === 'pending') return String(orderedRules[i].id)
    }
    return null
  }

  const moveCursor = (id, anchorMark = null) => {
    setIsEditing(false)
    setError(null)
    setShowLinkSearch(false)
    anchorMarkRef.current = anchorMark
    setCurrentRuleId(id)
  }

  // Capture the document order of rule marks once the sanitized HTML renders.
  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      setDocOrder((prev) => (prev.length === 0 ? prev : []))
      return
    }
    const seen = []
    const seenSet = new Set()
    container.querySelectorAll('mark[data-rule-id]').forEach((mark) => {
      const id = mark.dataset.ruleId
      if (!seenSet.has(id)) {
        seenSet.add(id)
        seen.push(id)
      }
    })
    setDocOrder((prev) =>
      prev.length === seen.length && prev.every((v, i) => v === seen[i]) ? prev : seen
    )
  }, [safeHtml])

  // When the parent triggers a review (the "Review Rules" prompt or a fresh
  // extraction), jump to the first pending rule in document order.
  useEffect(() => {
    if (!reviewTrigger) return
    const firstPending = orderedRules.find((r) => r.status === 'pending')
    const target = firstPending || orderedRules[0]
    if (target) moveCursor(String(target.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewTrigger])

  // Re-style every mark whenever statuses or the active cursor change.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    container.querySelectorAll('mark[data-rule-id]').forEach((mark) => {
      const id = mark.dataset.ruleId
      const rule = rulesById.get(id)
      const status = rule?.status || 'pending'
      const isCurrent = id === String(currentRuleId)
      const isLinked = (rule?.matched_analytics?.length || 0) > 0
      const isLinking = linking && linkingRuleIds.has(id)
      mark.className =
        `policy-mark policy-mark-${status}` +
        `${isCurrent ? ' policy-mark-current' : ''}` +
        `${isLinked ? ' policy-mark-linked' : ''}` +
        `${isLinking ? ' policy-mark-linking' : ''}`
      mark.style.cursor = 'pointer'
    })
  }, [safeHtml, rulesById, currentRuleId, linking, linkingRuleIds])

  // Stop showing the per-rule linking loader once matching finishes.
  useEffect(() => {
    if (!linking) setLinkingRuleIds(new Set())
  }, [linking])

  // Hide the floating "add rule" affordance when the text selection collapses.
  useEffect(() => {
    const onSelChange = () => {
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed) setSelection(null)
    }
    document.addEventListener('selectionchange', onSelChange)
    return () => document.removeEventListener('selectionchange', onSelChange)
  }, [])

  // Close the floating panel on any click outside it. The dimming backdrop only
  // covers the document Card, so clicks elsewhere on the page (left nav, page
  // wrapper) wouldn't otherwise dismiss the panel. Clicks on a rule mark are left
  // alone so they can select a different rule.
  useEffect(() => {
    if (!currentRuleId) return undefined
    const onMouseDown = (e) => {
      if (floatingBarRef.current?.contains(e.target)) return
      if (e.target.closest?.('mark[data-rule-id]')) return
      // Clicks inside an open modal (e.g. Compose Analytic, which portals to
      // <body>) must not collapse the rule panel — that would unmount the modal.
      if (e.target.closest?.('.modal, .modal-backdrop')) return
      setCurrentRuleId(null)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [currentRuleId])

  // Center the selected highlight on screen and float the panel just beneath it.
  // The panel's offset is measured in document space, so it stays glued to the
  // highlight as the page scrolls.
  useEffect(() => {
    const card = cardRef.current
    if (!card) return
    const marks = currentRuleId != null && containerRef.current
      ? Array.from(containerRef.current.querySelectorAll(`mark[data-rule-id="${CSS.escape(String(currentRuleId))}"]`))
      : []
    // If the user clicked a specific mark, anchor to it; otherwise (e.g. Next/Prev
    // navigation) fall back to the first mark for scrolling. A rule can have marks
    // in distant parts of the document, so anchoring to the clicked one keeps the
    // panel next to where the user actually clicked instead of jumping away.
    const clickedMark = anchorMarkRef.current && marks.includes(anchorMarkRef.current)
      ? anchorMarkRef.current
      : null
    const firstMark = clickedMark || marks[0] || null
    // Anchor beneath the clicked mark (or the bottom-most one) so the panel never
    // covers the highlight.
    const lastMark = clickedMark || marks.reduce(
      (acc, m) => (!acc || m.getBoundingClientRect().bottom > acc.getBoundingClientRect().bottom ? m : acc),
      null
    )

    const positionBar = () => {
      const cardTop = card.getBoundingClientRect().top
      if (lastMark) {
        setBarTop(lastMark.getBoundingClientRect().bottom - cardTop + 10)
      } else {
        // No highlight to anchor to: pin near the top of the current viewport.
        setBarTop(NAVBAR_HEIGHT + 12 - cardTop)
      }
    }

    const raf = requestAnimationFrame(() => {
      if (firstMark) {
        // Place the selected section about a third of the way down the screen.
        const top = firstMark.getBoundingClientRect().top + window.scrollY - window.innerHeight / 3
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
      }
      positionBar()
    })
    window.addEventListener('scroll', positionBar, { passive: true })
    window.addEventListener('resize', positionBar)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', positionBar)
      window.removeEventListener('resize', positionBar)
    }
  }, [currentRuleId, safeHtml])

  if (!html && !currentRule) return null

  const handleContainerClick = (e) => {
    const mark = e.target.closest('mark[data-rule-id]')
    if (!mark || !containerRef.current?.contains(mark)) return
    // Select the rule; moveCursor scrolls the clicked mark to center and floats
    // the panel just beneath it.
    moveCursor(mark.dataset.ruleId, mark)
  }

  // Text-selection → "Add as rule": capture the selection so the floating
  // affordance can offer to turn highlighted document text into a new rule.
  const handleMouseUp = () => {
    const sel = window.getSelection()
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
      setSelection(null)
      return
    }
    const range = sel.getRangeAt(0)
    if (!containerRef.current?.contains(range.commonAncestorContainer)) {
      setSelection(null)
      return
    }
    const text = sel.toString().trim()
    if (!text) {
      setSelection(null)
      return
    }
    const rect = range.getBoundingClientRect()
    savedRangeRef.current = range.cloneRange()
    setSelection({ text, top: rect.top, left: rect.left + rect.width / 2 })
  }

  const handleAddFromSelection = async () => {
    const range = savedRangeRef.current
    const text = selection?.text
    if (!range || !text || busy) return
    setBusy(true)
    setError(null)
    try {
      const newRule = await onCreateRule({
        category: nearestHeading(containerRef.current, range) || 'Added during review',
        content: text,
        position: orderedRules.length
      })
      if (newRule?.id != null) {
        markLinking([newRule.id])
        wrapRangeWithMarks(range, newRule.id)
        if (containerRef.current && onPersistHtml) {
          await onPersistHtml(containerRef.current.innerHTML)
        }
        moveCursor(String(newRule.id))
      }
      window.getSelection()?.removeAllRanges()
      savedRangeRef.current = null
      setSelection(null)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add rule.')
    } finally {
      setBusy(false)
    }
  }

  const runUpdate = async (data, advance) => {
    if (!currentRule || busy) return
    setBusy(true)
    setError(null)
    try {
      await onUpdateRule({ ruleId: currentRule.id, data })
      setIsEditing(false)
      if (advance) moveCursor(pickNext(currentRule.id))
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const markLinking = (ids) =>
    setLinkingRuleIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => next.add(String(id)))
      return next
    })

  // Re-run AI analytic matching for just the selected rule (used when it came
  // back with no linked analytics).
  const handleRetryMatch = async () => {
    if (!currentRule || !onRetryMatch || busy) return
    setBusy(true)
    setError(null)
    markLinking([currentRule.id])
    try {
      await onRetryMatch(currentRule.id)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to retry linking. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  // Manually link a chosen analytic to the selected rule.
  const handleManualLink = async (analyticId) => {
    if (!currentRule || !onLinkAnalytic || busy) return
    setBusy(true)
    setError(null)
    try {
      await onLinkAnalytic({ analyticId, ruleId: currentRule.id })
      setShowLinkSearch(false)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to link analytic. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleAccept = () => {
    if (currentRule) markLinking([currentRule.id])
    return runUpdate(
      {
        status: 'accepted',
        category: isEditing ? editCategory : currentRule.category,
        summary: isEditing ? editSummary : currentRule.summary
      },
      true
    )
  }
  const handleReject = () => runUpdate({ status: 'rejected' }, true)
  const handleRestore = () => runUpdate({ status: 'pending' }, false)

  const handleEdit = () => {
    setEditCategory(currentRule?.category || '')
    setEditSummary(currentRule?.summary || '')
    setIsEditing(true)
  }
  // Persist edits without changing status, so accepted rules stay accepted.
  const handleSaveEdit = () => runUpdate({ category: editCategory, summary: editSummary }, false)

  const handleDelete = async () => {
    if (!currentRule || busy) return
    if (!window.confirm('Delete this rule? This cannot be undone.')) return
    setBusy(true)
    setError(null)
    try {
      await onDeleteRule?.(currentRule.id)
      // Remove its highlight from the document and persist the cleaned HTML.
      const container = containerRef.current
      if (container && onPersistHtml) {
        const marks = container.querySelectorAll(`mark[data-rule-id="${CSS.escape(String(currentRule.id))}"]`)
        marks.forEach((m) => m.replaceWith(...m.childNodes))
        if (marks.length) await onPersistHtml(container.innerHTML)
      }
      setCurrentRuleId(null)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete rule.')
    } finally {
      setBusy(false)
    }
  }

  const handleAcceptAll = async () => {
    if (busy || pendingRules.length === 0) return
    markLinking(pendingRules.map((r) => r.id))
    setBusy(true)
    setError(null)
    try {
      await onBulkAccept(pendingRules.map((r) => r.id))
      moveCursor(null)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept rules.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="mb-3" ref={cardRef} style={{ position: 'relative' }}>
      <style>{MARK_STYLES}</style>

      {currentRule && (
        <div
          className="policy-doc-backdrop"
          onClick={() => setCurrentRuleId(null)}
        />
      )}

      {/* Single floating panel for the selected rule. */}
      {currentRule && (
        <div
          ref={floatingBarRef}
          className="bg-white policy-floating-bar"
          style={{ top: barTop }}
        >
          {error && (
            <Alert variant="danger" className="py-1 px-2 small mb-2" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {(
            <>
              <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                <span className="fw-bold text-truncate" style={{ fontSize: '1.05rem' }}>{currentRule.category}</span>
                {currentRule.status === 'accepted' && <AcceptedBadge />}
                {currentRule.status === 'pending' && <Badge bg="" className="badge-warning" style={{ fontSize: '0.65rem' }}>Pending</Badge>}
                {currentRule.status === 'rejected' && <Badge bg="" className="badge-secondary" style={{ fontSize: '0.65rem' }}>Rejected</Badge>}
                {currentIndex >= 0 && pendingRules.length > 0 && (
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                    Rule {currentIndex + 1} of {total} · {acceptedCount} accepted · {pendingRules.length} pending · {rejectedCount} rejected
                  </span>
                )}
                {!isEditing && pendingRules.length > 0 && (
                  <Button variant="outline-primary" size="sm" onClick={handleAcceptAll} disabled={busy}>
                    <FontAwesomeIcon icon={faCheckDouble} className="me-1" />Accept All ({pendingRules.length})
                  </Button>
                )}
              </div>

              {!isEditing && currentRule.summary && (
                <p className="text-muted fst-italic mb-2" style={{ fontSize: '0.9rem' }}>{currentRule.summary}</p>
              )}

              {(currentRule.matched_analytics?.length || 0) > 0 && (
                <div className="mb-2">
                  <div
                    className="text-uppercase text-muted fw-semibold mb-1"
                    style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}
                  >
                    Linked Analytics
                  </div>
                  {currentRule.matched_analytics.map((ma) => {
                    const analytic = allAnalytics.find((a) => a.id === ma.analytic_id)
                    return (
                      <div key={ma.id} className="py-1" style={{ borderTop: '1px solid #e3e8f0' }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="small">
                            <FontAwesomeIcon icon={faLink} className="text-muted me-1" style={{ fontSize: '0.7rem' }} />
                            {ma.analytic_name}
                            {ma.confidence != null && (
                              <Badge bg="" className={`ms-2 badge-${confidenceBg(ma.confidence)}`} style={{ fontSize: '0.65rem' }}>
                                {Math.round(ma.confidence * 100)}%
                              </Badge>
                            )}
                          </span>
                          <span className="d-flex align-items-center">
                            {ma.custom && municipalityId && (
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 ms-2 text-primary"
                                title="Edit recipe"
                                onClick={() => setEditAnalyticId(ma.analytic_id)}
                              >
                                <FontAwesomeIcon icon={faPen} style={{ fontSize: '0.7rem' }} />
                              </Button>
                            )}
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 ms-2 text-danger"
                              title="Remove link"
                              onClick={() => onUnlinkAnalytic?.({ linkId: ma.id })}
                            >
                              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.7rem' }} />
                            </Button>
                          </span>
                        </div>
                        {analytic?.description && (
                          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                            {renderDescription(analytic.description)}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {linking && linkingRuleIds.has(String(currentRule.id)) && (
                <div className="d-flex align-items-center justify-content-center gap-2 text-muted small py-3">
                  <Spinner animation="border" size="sm" />
                  Linking analytics...
                </div>
              )}

              {currentRule.status === 'accepted' &&
                !(linking && linkingRuleIds.has(String(currentRule.id))) && (
                  <div className="mb-2">
                    {(currentRule.matched_analytics?.length || 0) === 0 && (
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                        <span className="text-muted small fst-italic">No analytics linked.</span>
                        {onRetryMatch && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            disabled={busy}
                            onClick={handleRetryMatch}
                          >
                            <FontAwesomeIcon icon={faRedo} className="me-1" style={{ fontSize: '0.7rem' }} />
                            Retry linking
                          </Button>
                        )}
                      </div>
                    )}

                    {onLinkAnalytic && (showLinkSearch ? (
                      <AnalyticSearch
                        allAnalytics={allAnalytics}
                        linkedAnalyticIds={new Set((currentRule.matched_analytics || []).map((ma) => ma.analytic_id))}
                        onSelect={handleManualLink}
                        policySectionIds={policySectionIds}
                      />
                    ) : (
                      <div className="d-flex align-items-center gap-3">
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-decoration-none"
                          disabled={busy}
                          onClick={() => setShowLinkSearch(true)}
                        >
                          <FontAwesomeIcon icon={faPlus} className="me-1" style={{ fontSize: '0.7rem' }} />
                          Link analytics manually
                        </Button>
                        {canCompose && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            disabled={busy}
                            onClick={() => { setComposeRule(currentRule); setShowCompose(true) }}
                          >
                            <FontAwesomeIcon icon={faMagic} className="me-1" style={{ fontSize: '0.7rem' }} />
                            Compose Analytic
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              {isEditing && (
                <>
                  <Form.Control
                    type="text"
                    size="sm"
                    className="mb-1"
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    placeholder="Category"
                  />
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    value={editSummary}
                    onChange={(e) => setEditSummary(e.target.value)}
                    placeholder="Brief one-line summary"
                  />
                </>
              )}

              {/* Footer: all action buttons */}
              <div className="d-flex align-items-center justify-content-between gap-2 mt-2 pt-2 border-top">
                <div className="d-flex align-items-center gap-1">
                  {!isEditing && (
                    <>
                      <Button variant="light" size="sm" className="text-primary" disabled={currentIndex <= 0} onClick={() => moveCursor(String(orderedRules[currentIndex - 1].id))}>
                        <FontAwesomeIcon icon={faChevronUp} className="me-1" />Previous
                      </Button>
                      <Button variant="light" size="sm" className="text-primary" disabled={currentIndex < 0 || currentIndex >= total - 1} onClick={() => moveCursor(String(orderedRules[currentIndex + 1].id))}>
                        <FontAwesomeIcon icon={faChevronDown} className="me-1" />Next
                      </Button>
                    </>
                  )}
                </div>
                <div className="d-flex align-items-center gap-1 flex-wrap justify-content-end">
                  {isEditing ? (
                    <>
                      <Button variant="primary" size="sm" onClick={handleSaveEdit} disabled={busy}>
                        {busy ? <Spinner as="span" animation="border" size="sm" /> : <><FontAwesomeIcon icon={faSave} className="me-1" />Save</>}
                      </Button>
                      <Button variant="light" size="sm" onClick={() => setIsEditing(false)} disabled={busy}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      {currentRule.status === 'pending' && (
                        <>
                          <Button variant="primary" size="sm" onClick={handleAccept} disabled={busy}>
                            {busy ? <Spinner as="span" animation="border" size="sm" /> : <><FontAwesomeIcon icon={faCheck} className="me-1" />Accept</>}
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={handleReject} disabled={busy}>
                            <FontAwesomeIcon icon={faTimes} className="me-1" />Reject
                          </Button>
                        </>
                      )}
                      <Button variant="light" size="sm" className="text-primary" onClick={handleEdit}>
                        <FontAwesomeIcon icon={faPen} className="me-1" />Edit
                      </Button>
                      {currentRule.status === 'rejected' && (
                        <Button variant="outline-warning" size="sm" onClick={handleRestore} disabled={busy}>
                          <FontAwesomeIcon icon={faUndo} className="me-1" />Restore
                        </Button>
                      )}
                      <Button variant="light" size="sm" className="text-primary" onClick={() => setCurrentRuleId(null)}>
                        <FontAwesomeIcon icon={faTimes} className="me-1" />Close
                      </Button>
                      {currentRule.status !== 'pending' && (
                        <Button variant="outline-danger" size="sm" onClick={handleDelete} disabled={busy} title="Delete rule">
                          <FontAwesomeIcon icon={faTrash} className="me-1" />Delete
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <Card.Body
        style={{
          fontSize: '0.9rem',
          lineHeight: '1.6',
          wordBreak: 'break-word',
          paddingBottom: currentRule ? '320px' : undefined
        }}
      >
        {safeHtml ? (
          <div
            ref={containerRef}
            className="policy-doc-content"
            onClick={handleContainerClick}
            onMouseUp={handleMouseUp}
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        ) : (
          <p className="text-muted small fst-italic mb-0">
            The formatted document view isn&apos;t available yet. You can still review each rule above.
          </p>
        )}
      </Card.Body>

      {selection && (
        <div
          style={{
            position: 'fixed',
            top: Math.max(8, selection.top - 40),
            left: selection.left,
            transform: 'translateX(-50%)',
            zIndex: 1080
          }}
        >
          <Button
            size="sm"
            variant="primary"
            className="shadow-sm"
            disabled={busy}
            // Keep the text selection alive when the button takes focus.
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleAddFromSelection}
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />Add as rule
          </Button>
        </div>
      )}

      {showCompose && composeRule && (
        <ComposeAnalyticModal
          show={showCompose}
          onHide={() => setShowCompose(false)}
          municipalityId={municipalityId}
          sections={composableSections}
          defaultSectionId={defaultSectionId}
          seedDescription={composeRule.content}
          onSaved={(analytic) => {
            setShowCompose(false)
            if (onLinkAnalytic) onLinkAnalytic({ analyticId: analytic.id, ruleId: composeRule.id })
          }}
        />
      )}
      {editAnalyticId && (
        <ComposeAnalyticModal
          show={!!editAnalyticId}
          onHide={() => setEditAnalyticId(null)}
          municipalityId={municipalityId}
          analyticId={editAnalyticId}
          sections={composableSections}
          onSaved={() => setEditAnalyticId(null)}
        />
      )}
    </Card>
  )
}

// Text of the nearest heading appearing before the selection, used as a default
// category for a rule added from highlighted text.
const nearestHeading = (container, range) => {
  if (!container) return null
  let result = null
  container.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((h) => {
    if (h.compareDocumentPosition(range.startContainer) & Node.DOCUMENT_POSITION_FOLLOWING) {
      result = h.textContent.trim()
    }
  })
  return result || null
}

// Wrap every text node inside a range in <mark data-rule-id> elements (one per
// node, so marks never cross block boundaries), splitting the boundary nodes so
// only the selected text is highlighted.
const wrapRangeWithMarks = (range, ruleId) => {
  if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset > 0) {
    range.setStart(range.startContainer.splitText(range.startOffset), 0)
  }
  if (range.endContainer.nodeType === Node.TEXT_NODE && range.endOffset < range.endContainer.length) {
    range.endContainer.splitText(range.endOffset)
  }
  const rootEl =
    range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? range.commonAncestorContainer.parentNode
      : range.commonAncestorContainer
  const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      range.intersectsNode(node) && node.textContent && node.textContent.trim()
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT
  })
  const textNodes = []
  while (walker.nextNode()) textNodes.push(walker.currentNode)
  textNodes.forEach((node) => {
    if (node.parentNode?.nodeName === 'MARK') {
      node.parentNode.setAttribute('data-rule-id', String(ruleId))
      return
    }
    const mark = document.createElement('mark')
    mark.setAttribute('data-rule-id', String(ruleId))
    node.parentNode.insertBefore(mark, node)
    mark.appendChild(node)
  })
}

// Whether the rendered document actually contains a mark for the given rule id.
const containerHasMark = (containerRef, ruleId) => {
  if (ruleId == null || !containerRef.current) return true
  return !!containerRef.current.querySelector(`mark[data-rule-id="${CSS.escape(String(ruleId))}"]`)
}

export default PolicyDocumentContent
